import { useCallback, useEffect, useMemo, useState } from "react";
import { useAxios } from "../../common_nextgen/axios/AxiosHelper";
import FunctionKeys from "../../common_nextgen/common_components/FunctionKeys/FunctionKeys";
import useCustomNavigate from "../../common_nextgen/common_components/Navigation/NextGenNavigation";
import Form from "../../common_nextgen/common_components/form/Form";
import { useToast } from "../../common_nextgen/hooks/ToastContext";
import {
  PRICE_SCHEDULE_MODES,
  NO_CHANGE_OF_INFO,
  ROUTE_PATHS,
} from "../../common_nextgen/utils/Constants";
import {
  isAddMode,
  isEditMode,
  isReadOnlyMode,
} from "../../common_nextgen/utils/UtilFunctions";
import { formFields, screenInfo } from "./BenefitMaxScheduleConfig";
import useCrossAppSlices from "../../common_nextgen/common_components/common-hooks/useCrossAppSlices";
import useAiProgramBackNavigation from "../../common_nextgen/common_components/common-hooks/useAiProgramBackNavigation";

const BenefitMaxScheduleForm = ({ mode }) => {
  const { axios_helper_no_header } = useAxios();
  const navigateToPage = useCustomNavigate();
  const [constraintErrors, setConstraintErrors] = useState([]);
  const toast = useToast();

  const {
    currentState,
    previousState,
    setStateVal,
    currentMode,
    setCurrentMode,
    initialFormData,
    setInitialFormData,
    lookupData,
    fetchLookupData,
    viewLookupDetails,
    currentIndex,
    setHandleBack,
    onFetchInitialData,
    isNoChangeOfInfo,
    location,
    externalMode,
    paramsData,
  } = useCrossAppSlices({
    mode,
    recordsKey: "selectedPriceSchedules",
  });

  const {
    selectedPriceSchedules = [],
    previousScreen = ROUTE_PATHS.BENEFIT_MAX_SCHEDULE,
    message = "",
    returnProgramCode = "",
    mediatorId = "",
  } = currentState;

  const { isAiProgramReturn, handleBackToAiProgram } =
    useAiProgramBackNavigation({
      programCode: returnProgramCode,
      mediatorId,
      paramsData,
    });

  const customFetchLookupData = (lookupScreen, params, formData) => {
    const updatedFormData = { ...formData };
    if (lookupScreen.default === "RCPLN018" && formData.planCode === "") {
      updatedFormData.planEffDate = "";
      updatedFormData.planName = "";
    }
    fetchLookupData(lookupScreen, params, updatedFormData);
  };

  const handleBack = () => {
    if (externalMode) {
      const message = {
        message: "navigate-to-external-program",
        url: previousScreen,
        parameters: {
          state: [...previousState],
        },
      };
      window.parent.postMessage(message, "*");
    } else if (currentIndex < selectedPriceSchedules.length - 1) {
      setHandleBack();
    } else if (
      message === "navigate-to-ai-new-program" &&
      isAiProgramReturn
    ) {
      handleBackToAiProgram();
    } else {
      navigateToPage(previousScreen, previousState);
    }
  };

  const getPageTitle = useCallback(() => {
    const [screenTitle, screenNumber] = isAddMode(currentMode)
      ? screenInfo.ADD
      : screenInfo.CHANGE;
    return (
      <>
        <span className="title">{screenTitle}</span>
        <span>{"-"}</span>
        <span className="screen-number">{screenNumber}</span>
      </>
    );
  }, [currentMode]);

  const submitForm = async (dataForSubmission) => {
    const url = "api/bscBenefitSchCtrl";
    let verb = "";
    let screenNumber = "";
    let infoChanged = true;

    if (isAddMode(currentMode)) {
      verb = "POST";
      screenNumber = screenInfo.ADD[1];
    } else {
      verb = "PUT";
      screenNumber = screenInfo.CHANGE[1];

      if (isNoChangeOfInfo(dataForSubmission)) {
        infoChanged = false;
      }
    }

    const data = { ...dataForSubmission, screenNumber, infoChanged };

    try {
      const { responseStatus, responseData } = await axios_helper_no_header(
        verb,
        url,
        data,
        null,
      );
      const severity =
        responseStatus.message === NO_CHANGE_OF_INFO ? "info" : "success";
      toast.current.show({
        severity,
        summary: "",
        detail: responseStatus.message,
        life: 3000,
      });

      setInitialFormData(responseData);
      if (isAddMode(currentMode)) {
        setCurrentMode(PRICE_SCHEDULE_MODES.EDIT);
        const lastRecord = previousState.pop() ?? {};
        const latestCurrentState = {
          ...currentState,
          selectedPriceSchedules: [{ ...responseData }],
          currentIndex: 0,
          externalMode: false,
        };
        const updatedState = [
          ...previousState,
          {
            ...lastRecord,
            paramsData: {
              bscScheduleName: responseData.bscScheduleName,
              hiddenRecordIndicator: "Active",
            },
          },
          latestCurrentState,
        ];
        setStateVal(updatedState);
        navigateToPage(ROUTE_PATHS.BENEFIT_MAX_SCHEDULE_EDIT, updatedState);
      }
    } catch (error) {
      const {
        responseStatus: { displayErrorFields = null, message },
      } = error.data;
      if (displayErrorFields === null) {
        toast.current.show({
          severity: "error",
          summary: "",
          detail: message,
          life: 3000,
        });
      } else {
        setConstraintErrors(displayErrorFields);
      }
    }
  };

  const navigateToSteps = () =>
    navigateToPage(ROUTE_PATHS.BENEFIT_MAX_STEP_SCHEDULE_SEARCH, [
      ...previousState,
      { ...currentState, currentIndex, currentMode, externalMode },
      {
        selectedPriceSchedules: [selectedPriceSchedules[currentIndex]],
        currentIndex: 0,
        previousScreen: location.pathname,
      },
    ]);

  const navigateToNote = () =>
    navigateToPage(ROUTE_PATHS.BENEFIT_MAX_SCHEDULE_NOTE, [
      ...previousState,
      { ...currentState, currentIndex, currentMode, externalMode },
      {
        selectedPriceSchedules: [selectedPriceSchedules[currentIndex]],
        previousScreen: location.pathname,
        description: initialFormData?.bscDescription,
      },
    ]);

  const [keyFunc, keyName] = useMemo(() => {
    const keyFunc = {};
    const keyName = {};
    if (isAddMode(currentMode)) {
      keyFunc.F12 = handleBack;
      keyName.F12 = "Back";
    } else if (isEditMode(currentMode)) {
      keyFunc.F12 = handleBack;
      keyName.F12 = "Back";
      keyFunc.Chmode = () => setCurrentMode(PRICE_SCHEDULE_MODES.READ_ONLY);
      keyName.Chmode = "View";
      keyFunc.F7 = navigateToSteps;
      keyName.F7 = "Steps";
      keyFunc.F14 = navigateToNote;
      keyName.F14 = "Note";
    } else if (isReadOnlyMode(currentMode)) {
      keyFunc.F12 = handleBack;
      keyName.F12 = "Back";
      keyFunc.Chmode = () => setCurrentMode(PRICE_SCHEDULE_MODES.EDIT);
      keyName.Chmode = "Edit";
      keyFunc.F7 = navigateToSteps;
      keyName.F7 = "Steps";
      keyFunc.F14 = navigateToNote;
      keyName.F14 = "Note";
    }

    return [keyFunc, keyName];
  }, [currentMode, currentIndex, externalMode, selectedPriceSchedules]);

  useEffect(() => {
    const fetchInitialData = async () => {
      if (
        !isAddMode(currentMode) &&
        !selectedPriceSchedules?.[currentIndex]
      )
        return;

      const params = isAddMode(currentMode) ? {} : { notesRequiredFlag: true };
      const url = isAddMode(currentMode)
        ? "api/bscBenefitSchCtrl/initialFormData/RCBSC003"
        : `api/bscBenefitSchCtrl/${encodeURIComponent(
            selectedPriceSchedules[currentIndex].bscScheduleName,
          )}`;
      try {
        const { responseData } = await axios_helper_no_header(
          "GET",
          url,
          {},
          params,
        );
        setInitialFormData(responseData);
      } catch (e) {
        console.error(e);
      }
    };

    onFetchInitialData(fetchInitialData);
  }, [currentIndex, currentMode, selectedPriceSchedules, onFetchInitialData]);

  return (
    <div className="grid container">
      <div className="col col-12">
        <FunctionKeys keyFunc={keyFunc} keyName={keyName} />
      </div>
      <div className="col col-12">
        <h1>{getPageTitle()}</h1>
      </div>
      <div className="col col-12">
        <Form
          formData={initialFormData}
          formFields={formFields}
          mode={currentMode}
          submitFormData={submitForm}
          constraintErrors={constraintErrors}
          lookupData={lookupData}
          fetchLookupData={customFetchLookupData}
          viewLookupDetails={viewLookupDetails}
        />
      </div>
    </div>
  );
};

export default BenefitMaxScheduleForm;
